import create from 'zustand';
import produce from 'immer';
import { Shipment, ShippingMethod } from '~/lib/data-contract/salesforce';
import {
    CustomerInformationInputs,
    InstoreInformationInputs,
    StoreDeliveryAddress,
    PayloadDeliveryAddress,
    DropPointDeliveryAddress,
    AlternativeDeliveryAddress,
    BaseDeliveryAddress,
} from '../models/fields';
import { Carrier, Carriers, DeliveryMethod } from '../models/checkout';
import { getDeliveryMethods } from '../utils/getDeliveryMethod';
import React from 'react';

//Model for different flow states
type Address = BaseDeliveryAddress | CustomerInformationInputs | InstoreInformationInputs;
type Step = {
    title: string;
    component?: React.ReactNode;
};

export type CheckoutState = {
    // current step
    currentStep: number;

    changingCarrier: boolean;
    // set current Step
    setCurrentStep: (currentStep: number) => void;

    goForward: () => void | null;
    goBack: () => void;

    // Checkout Flow
    billingAddress?: Address;
    deliveryAddress?: BaseDeliveryAddress | null;
    deliveryMethod: DeliveryMethod | undefined;
    setBillingAddress: (payload: Address) => void;
    setDeliveryAddress: (payload: PayloadDeliveryAddress | null) => void;
    setDeliveryMethod: (payload: DeliveryMethod) => void;
    zipCode: string;

    // set current Step
    setZipCode: (postalCode: string) => void;

    carriers: Carriers | null;
    carrier?: Carrier;
    setCarrier: (carrier?: ShippingMethod, deliveryMethod?: DeliveryMethod) => void;
    setCarriers: (carriers?: Carriers) => void;
    shipmentId: string;
    setShipmentId: (shipmentId: string) => void;
    isFormInEditMode: boolean;
    setIsFormInEditMode: (payload: boolean) => void;
    getCurrentShipmentIndex: (shipments?: Shipment[]) => number;
    setNumberOfShipments: (amount: number) => void;
    numberOfShipments: number;
    currentShipmentStep: number;
    setCurrentShipmentStep: (amount: number) => void;
    isAlternateShippingValid: boolean;
    setAlternateShippingValid: (payload: boolean) => void;
    setIsUsingAlternateAddress: (payload: boolean) => void;
    isUsingAlternateAddress: boolean;
    alternateShippingAddress: any;
    onBackToStart: () => void;
    setOnBackToStart: (func: () => void) => void;
    deliveryDate: string;
    setDeliveryDate: (date: string) => void;
    vendorSelectedDroppoint: boolean;
    setVendorSelectedDroppoint: (payload: boolean) => void;
    setAlternateShippingAddress: (payload: any) => void;
    setChangingCarrier: (payload: boolean) => void;
    steps: Step[];
    setSteps: (steps: Step[]) => void;
};

/**
 * Global and shared store for Checkout Steps
 * Avoid using directly - use the `useCheckout` hook instead
 */
export const useCheckoutState = create<CheckoutState>((set, get) => ({
    currentStep: 0,
    carriers: null,
    carrier: undefined,
    changingCarrier: false,
    deliveryAddress: null,
    shipmentId: '',
    numberOfShipments: 1,
    currentShipmentStep: 1,
    isAlternateShippingValid: false,
    alternateShippingAddress: {},
    isValid: false,
    vendorSelectedDroppoint: false,
    steps: [],
    isFormInEditMode: false,
    setIsFormInEditMode: (payload: boolean) => set({ isFormInEditMode: payload }),
    setSteps: (steps: Step[]) => set({ steps }),
    setChangingCarrier: (payload: boolean) => set({ changingCarrier: payload }),
    setVendorSelectedDroppoint: (payload: boolean) => set({ vendorSelectedDroppoint: payload }),
    onBackToStart: () => {
        return;
    },
    setOnBackToStart: (func: () => void) => set({ onBackToStart: func }),
    setAlternateShippingValid: (isValid) => set({ isAlternateShippingValid: isValid }),
    setAlternateShippingAddress: (payload: any) => set({ alternateShippingAddress: payload }),
    setNumberOfShipments: (amount: number) => set({ numberOfShipments: amount }),
    setCurrentShipmentStep: (amount: number) => {
        set({ currentShipmentStep: amount });
    },
    getCurrentShipmentIndex: (shipments?: Shipment[]) => {
        if (!shipments || shipments.length === 0) return 0;
        const shipmentId = get().shipmentId;

        const index = shipments.map((shipment) => shipment.shipmentId).indexOf(shipmentId);
        return index !== -1 ? index + 1 : 1; // Ensure the smallest index is 1
    },

    setShipmentId: (shipmentId: string) => set({ shipmentId }),
    setCarrier: (carrier, deliveryMethod) => {
        if (!carrier) {
            set({ carrier: undefined });
            return;
        }

        const deliveryMethods = getDeliveryMethods(carrier);
        const currentDeliveryMethod = deliveryMethod || deliveryMethods;

        if (!currentDeliveryMethod) return;

        if (!get().zipCode) {
            set(
                produce((draft) => {
                    draft.zipCode = get().billingAddress?.postalCode || '';
                    if (!draft.carrier) {
                        draft.carrier = {};
                    }
                    draft.carrier[currentDeliveryMethod] = carrier;
                }),
            );
        } else {
            set(
                produce((draft) => {
                    if (!draft.carrier) {
                        draft.carrier = {};
                    }
                    draft.carrier[currentDeliveryMethod] = carrier;
                }),
            );
        }

        if (
            carrier.c_shippingType === 'TRUCK' ||
            carrier.c_shippingType === 'VIRTUAL_DELIVERY' ||
            get().vendorSelectedDroppoint
        ) {
            const billingAddress = get().billingAddress;
            if (billingAddress) {
                const deliveryAddress = {
                    firstName: billingAddress?.firstName,
                    lastName: billingAddress?.lastName,
                    address1: billingAddress?.address1,
                    city: billingAddress?.city,
                    postalCode: billingAddress?.postalCode,
                    phone: billingAddress?.phoneMobile,
                    deliveryType: 'alternative',
                };
                set(
                    produce((draft) => {
                        if (!draft.deliveryAddress) {
                            draft.deliveryAddress = {};
                        }
                        draft.deliveryAddress = deliveryAddress;
                    }),
                );
            }
        }
    },
    setCarriers: (carriers) => {
        if (!carriers) {
            return set({ carriers: null });
        }

        Object.entries(carriers).forEach(([key, value]) => {
            if (value.length === 1) {
                set(
                    produce((draft) => {
                        if (!draft.carrier) {
                            draft.carrier = {};
                        }
                        draft.carrier[key] = value[0];
                    }),
                );
            }
        });

        set({ carriers });
    },
    setCurrentStep: (currentStep) => set({ currentStep }),
    goForward: () => {
        if (get().currentStep === get().steps.length) {
            return null;
        }
        set({
            currentStep: get().currentStep + 1,
        });
    },
    goBack: () => {
        if (get().currentStep === 0) {
            return get().onBackToStart();
        }

        set({
            currentStep: get().currentStep - 1,
        });
    },
    setBillingAddress: (payload: Address) => set({ billingAddress: payload }),
    setDeliveryAddress: (payload: PayloadDeliveryAddress | null) => {
        if (payload === null) {
            set(
                produce((draft) => {
                    draft.deliveryAddress = {};
                }),
            );
            return;
        }

        const { deliveryMethod, billingAddress, carrier } = get();
        if (!payload || !deliveryMethod) {
            return;
        }

        let deliveryAddress = {
            firstName: payload.name,
            lastName: ' ',
            address1: payload.address1,
            address2: payload.address2,
            city: payload.city,
            countryCode: payload.countryCode,
            postalCode: payload.zip || payload.postalCode,
            //@ts-ignore
            phone: billingAddress?.phoneMobile || '',
            _id: payload._id || payload.id,
            deliveryType: payload._type ?? '',
        };

        // Handle specific fields based on _type
        if (payload._type === 'store') {
            (deliveryAddress as StoreDeliveryAddress).storeId = payload.id;
        } else if (payload._type === 'droppoint') {
            (deliveryAddress as DropPointDeliveryAddress).storeId = payload.dropPointId;
            const carrierId = carrier?.[deliveryMethod]?.id;
            if (carrierId) {
                (deliveryAddress as DropPointDeliveryAddress).carrier = { id: carrierId };
            } else {
                (deliveryAddress as DropPointDeliveryAddress).carrier = {};
            }

            if ((deliveryAddress as DropPointDeliveryAddress).carrier) {
                (deliveryAddress as DropPointDeliveryAddress).carrierCode = payload.carrierCode;
            }
        } else if (!payload._type) {
            deliveryAddress = {
                firstName: payload?.firstName,
                lastName: payload?.lastName,
                address1: payload?.address1,
                address2: payload?.address2,
                _id: payload?.id,
                countryCode: payload?.countryCode,
                city: payload?.city,
                postalCode: payload?.postalCode,
                phone: payload?.phoneMobile ?? payload?.phone ?? '',
                deliveryType: 'alternative',
            };

            const carrierId = carrier?.[deliveryMethod]?.id;
            if (carrierId) {
                (deliveryAddress as AlternativeDeliveryAddress).carrier = { id: carrierId };
            } else {
                (deliveryAddress as AlternativeDeliveryAddress).carrier = {};
            }

            if ((deliveryAddress as AlternativeDeliveryAddress).carrier) {
                (deliveryAddress as AlternativeDeliveryAddress).carrierCode = payload.carrierCode;
            }
        }

        set(
            produce((draft) => {
                draft.deliveryAddress = deliveryAddress;
            }),
        );
    },

    deliveryMethod: undefined,
    setDeliveryMethod: (payload: DeliveryMethod) => set({ deliveryMethod: payload }),
    zipCode: '',
    setZipCode: (zipCode) => set({ zipCode }),
    deliveryDate: '',
    setDeliveryDate: (date) => set({ deliveryDate: date }),
    isUsingAlternateAddress: false,
    setIsUsingAlternateAddress: (isUsingAlternateAddress) => set({ isUsingAlternateAddress }),
}));
